import { cn } from "@/lib/utils";
import forgeMark from "@/assets/forge-mark.svg";
import forgeMarkOnLight from "@/assets/forge-mark-on-light.svg";

interface ForgeLogoProps {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  /**
   * The ground the mark sits on. "auto" (default) follows the app theme: gunmetal in
   * light mode, steel in dark mode. Pass "dark" or "light" for a surface that keeps its
   * own colour regardless of theme (e.g. an always-dark hero), so the mark keeps contrast.
   */
  surface?: "auto" | "light" | "dark";
}

const LABEL = "Forge logo";

/** The Forge mark (the metallic cross-sword): steel on dark grounds, gunmetal on light. */
export function ForgeLogo({ className, size = "md", surface = "auto" }: ForgeLogoProps) {
  const sizeMap = {
    sm: { classes: "w-6 h-6", px: 24 },
    md: { classes: "w-8 h-8", px: 32 },
    lg: { classes: "w-10 h-10", px: 40 },
    xl: { classes: "w-12 h-12", px: 48 },
  };

  const { classes, px } = sizeMap[size];
  const imgProps = { width: px, height: px };

  if (surface !== "auto") {
    return (
      <img
        {...imgProps}
        alt={LABEL}
        src={surface === "dark" ? forgeMark : forgeMarkOnLight}
        className={cn(classes, "object-contain", className)}
      />
    );
  }

  // Exactly one of these is displayed. A display:none image is removed from the
  // accessibility tree, so both carry the label and screen readers hear it once.
  return (
    <>
      <img {...imgProps} alt={LABEL} src={forgeMarkOnLight} className={cn(classes, "object-contain dark:hidden", className)} />
      <img {...imgProps} alt={LABEL} src={forgeMark} className={cn(classes, "hidden object-contain dark:block", className)} />
    </>
  );
}
